import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Type, Mail, Phone, MessageSquare, Wifi, Contact, Calendar, Smartphone, Share2, MapPin, CreditCard, MoreHorizontal, type LucideIcon } from 'lucide-react';
import { useAppStore, type PayloadType } from '../../store/useAppStore';
import { QRUpload } from './QRUpload';
import './PayloadTypeSelector.css';

interface TypeDef { id: PayloadType; icon: LucideIcon; tKey: string; groupKey?: string; }

const ALL_TYPES: TypeDef[] = [
  // Primary
  { id: 'url', icon: Link, tKey: 'create.types.url', groupKey: 'create.typeGroups.basic' },
  { id: 'text', icon: Type, tKey: 'create.types.text', groupKey: 'create.typeGroups.basic' },
  { id: 'email', icon: Mail, tKey: 'create.types.email', groupKey: 'create.typeGroups.basic' },
  { id: 'phone', icon: Phone, tKey: 'create.types.phone', groupKey: 'create.typeGroups.basic' },
  
  // Messaging
  { id: 'sms', icon: MessageSquare, tKey: 'create.types.sms', groupKey: 'create.typeGroups.messaging' },
  { id: 'social', icon: Share2, tKey: 'create.types.social', groupKey: 'create.typeGroups.messaging' },
  
  // Identity
  { id: 'vcard', icon: Contact, tKey: 'create.types.vcard', groupKey: 'create.typeGroups.identity' },
  { id: 'calendar', icon: Calendar, tKey: 'create.types.calendar', groupKey: 'create.typeGroups.identity' },
  { id: 'payment', icon: CreditCard, tKey: 'create.types.payment', groupKey: 'create.typeGroups.identity' },
  
  // Utility
  { id: 'wifi', icon: Wifi, tKey: 'create.types.wifi', groupKey: 'create.typeGroups.utility' },
  { id: 'appstore', icon: Smartphone, tKey: 'create.types.appstore', groupKey: 'create.typeGroups.utility' },
  { id: 'geo', icon: MapPin, tKey: 'create.types.geo', groupKey: 'create.typeGroups.utility' },
];

const GROUP_ORDER = [
  'create.typeGroups.basic',
  'create.typeGroups.messaging',
  'create.typeGroups.identity',
  'create.typeGroups.utility',
];

export const PayloadTypeSelector: React.FC = () => {
  const { t } = useTranslation('create');
  const payloadType = useAppStore(state => state.payloadType);
  const setPayloadType = useAppStore(state => state.setPayloadType);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scrollRef.current) return;
    const observer = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(scrollRef.current);
    return () => { observer.disconnect(); };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  // Dynamic visible count based on width
  const TAB_WIDTH = 85;
  const MORE_WIDTH = 85;
  const CONTAINER_PADDING = 16;
  const SCAN_WIDTH = 85; // Roughly the width of the scan tab
  const DIVIDER_WIDTH = 17; // 1px line + 16px horizontal margins
  
  let maxVisible = 4; // Default SSR
  if (containerWidth > 0) {
    const available = containerWidth - CONTAINER_PADDING - SCAN_WIDTH - DIVIDER_WIDTH;
    const maxWithoutMore = Math.floor(available / TAB_WIDTH);
    if (maxWithoutMore >= ALL_TYPES.length) {
      maxVisible = ALL_TYPES.length;
    } else {
      maxVisible = Math.max(1, Math.floor((available - MORE_WIDTH) / TAB_WIDTH));
    }
  }

  // Ensure active payload type is visible
  const activeIndex = ALL_TYPES.findIndex(t => t.id === payloadType);
  let finalVisibleCount = maxVisible;
  let forceShowActive = false;
  
  if (activeIndex >= maxVisible && maxVisible < ALL_TYPES.length) {
    finalVisibleCount = Math.max(1, maxVisible - 1);
    forceShowActive = true;
  }

  const visibleTabs = ALL_TYPES.slice(0, finalVisibleCount);
  const moreTabsRaw = ALL_TYPES.slice(finalVisibleCount);
  
  const moreTabs = forceShowActive
    ? moreTabsRaw.filter(t => t.id !== payloadType)
    : moreTabsRaw;

  const activeSecondary = forceShowActive ? ALL_TYPES[activeIndex] : null;

  // Group moreTabs
  const groupedMoreTabs: Record<string, TypeDef[] | undefined> = {};
  moreTabs.forEach(tab => {
    const key = tab.groupKey ?? 'create.typeGroups.other';
    groupedMoreTabs[key] ??= [];
    groupedMoreTabs[key].push(tab);
  });

  const activeGroups = GROUP_ORDER.filter(key => {
    const group = groupedMoreTabs[key];
    return group && group.length > 0;
  });

  return (
    <div className="payload-type-selector">
      <div className="tabs-container" ref={scrollRef}>
        <QRUpload />
        <div className="payload-divider" />
        {visibleTabs.map(({ id, icon: Icon, tKey }) => (
          <button
            key={id}
            className={`type-tab ${payloadType === id ? 'active' : ''}`}
            onClick={() => { setPayloadType(id); }}
            aria-label={t(tKey, id)}
            aria-pressed={payloadType === id}
          >
            <Icon size={18} />
            <span>{t(tKey, id.charAt(0).toUpperCase() + id.slice(1))}</span>
          </button>
        ))}

        {activeSecondary && (
          <button
            className="type-tab active"
            aria-pressed={true}
          >
            <activeSecondary.icon size={18} />
            <span>{t(activeSecondary.tKey, activeSecondary.id.charAt(0).toUpperCase() + activeSecondary.id.slice(1))}</span>
          </button>
        )}

        {moreTabs.length > 0 && (
          <div className="type-tab-more-wrapper" ref={menuRef}>
            <button
              className={`type-tab more-trigger ${menuOpen ? 'open' : ''}`}
              onClick={() => { setMenuOpen(prev => !prev); }}
              aria-expanded={menuOpen}
              aria-haspopup="true"
              type="button"
            >
              <MoreHorizontal size={18} />
              <span>{t('create.typeGroups.more', 'More')}</span>
            </button>

            {menuOpen && (
              <div className="type-menu">
                {activeGroups.map(groupKey => {
                  const fallbackLabel = groupKey.split('.').pop() ?? groupKey;
                  const displayLabel = fallbackLabel.charAt(0).toUpperCase() + fallbackLabel.slice(1);
                  return (
                    <div key={groupKey} className="type-menu-group">
                      <div className="type-menu-group-label">
                        {t(groupKey, displayLabel)}
                      </div>
                      {(groupedMoreTabs[groupKey] ?? []).map(({ id, icon: Icon, tKey }) => (
                        <button
                          key={id}
                          className={`type-menu-item ${payloadType === id ? 'active' : ''}`}
                          onClick={() => { setPayloadType(id); setMenuOpen(false); }}
                          aria-pressed={payloadType === id}
                        >
                          <Icon size={16} />
                          <span>{t(tKey, id.charAt(0).toUpperCase() + id.slice(1))}</span>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
